import { GetStaticPaths, GetStaticProps, GetStaticPropsContext } from 'next';
import { PHASE_PRODUCTION_BUILD } from 'next/constants';
import Page from '../../components/pages/product/Page';

// Product API
import type { ProductMapType } from '@virginexperiencedays/products';

// Utils
import { PromoService, vedPromosConfig } from '@virginexperiencedays/promos';
import { ISR_CONTROL } from '../../constants/revalidate';
import { PAGE_TYPES } from '../../components/tracking/gtm/consts';
import { fetchSlugNames } from '../../utils/product/fetchSlugNames';
import { isPageDarkTheme } from '../../utils/page';
import { fetchVedNavigationItems } from '../../utils/precache/navigation-items';
import { filterNonCollectionProducts } from '../../utils/filterCollectionProducts';
import { getTopProducts } from '../../utils/getTopProducts';
import { newProductsCache as productsCache, categoryDocumentsCache } from '../../utils/cache';
import { fetchReviewsByProductSlug, revalidate } from '../../utils/product/revalidate';

const navigationServiceUrl = process?.env?.NEXT_PUBLIC_NAVIGATION_SERVICE_BASE_URL;

export const getStaticProps: GetStaticProps = async ({ params }: GetStaticPropsContext) => {
  const slug = params?.slug as string;

  // Grab always from cache, as those are statically hard-coded
  const topProducts = await getTopProducts();

  if (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD) {
    const allProducts: ProductMapType = await productsCache.get('allProducts');
    const filteredProducts: ProductMapType = filterNonCollectionProducts(allProducts);
    const product = filteredProducts?.[slug];

    if (!product) {
      return {
        notFound: true,
      };
    }

    // Product is already fetched, fetch slugNameMap here
    const { slugNameMap, slugBreadcrumbNameMap } = await fetchSlugNames(
      product,
      categoryDocumentsCache
    );

    const reviews = await fetchReviewsByProductSlug(slug);
    const initialNavItems = await fetchVedNavigationItems(navigationServiceUrl);

    const promoService = new PromoService({
      activePromos: String(process.env.NEXT_PUBLIC_ACTIVE_PROMOS ?? ''),
      promosConfiguration: vedPromosConfig,
    });

    return {
      props: {
        trackingPageType: PAGE_TYPES.Product,
        isDarkTheme: isPageDarkTheme({ product }),
        product,
        topProducts,
        reviews,
        isActive: product.status === 'active',
        slugNameMap,
        slugBreadcrumbNameMap,
        initialNavItems,
        productPromo: promoService.getProductPromo(product),
      },
      revalidate: ISR_CONTROL.ONCE_PER_DAY,
    };
  }

  return revalidate({ slug, topProducts, path: `product/${slug}` });
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export default Page;
